interface Point {
  lat: number;
  lon: number;
  bad: boolean;
  label: string;
}

/**
 * Lightweight sign-in location map. Fits an equirectangular projection to the
 * points so a distant outlier (e.g. a Tor exit in Kyiv against Florida logins)
 * is immediately visible. Real cached OSM/Protomaps tiles via a Worker are §4.21
 * / Phase 4; this shows the geography without an external tile fetch.
 */
export function SigninMap({ points }: { points: Point[] }) {
  const W = 520;
  const H = 240;
  const pad = 24;

  if (points.length === 0) {
    return <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: "#8a8e88", fontSize: 12.5, background: "#f6f5f1", borderRadius: 6 }}>No sign-in locations.</div>;
  }

  const lats = points.map((p) => p.lat);
  const lons = points.map((p) => p.lon);
  let minLat = Math.min(...lats), maxLat = Math.max(...lats);
  let minLon = Math.min(...lons), maxLon = Math.max(...lons);
  // Minimum span so a single cluster still reads as a small-area map.
  const spanLat = Math.max(maxLat - minLat, 4);
  const spanLon = Math.max(maxLon - minLon, 4);
  const cLat = (minLat + maxLat) / 2, cLon = (minLon + maxLon) / 2;
  minLat = cLat - spanLat / 2; maxLat = cLat + spanLat / 2;
  minLon = cLon - spanLon / 2; maxLon = cLon + spanLon / 2;

  const x = (lon: number) => pad + ((lon - minLon) / (maxLon - minLon)) * (W - 2 * pad);
  const y = (lat: number) => pad + (1 - (lat - minLat) / (maxLat - minLat)) * (H - 2 * pad);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", background: "#eef2f0", borderRadius: 6 }} role="img" aria-label="Sign-in location map">
      {/* graticule */}
      {[0.25, 0.5, 0.75].map((f) => (
        <g key={f} stroke="#dfe5e2" strokeWidth={1}>
          <line x1={pad + f * (W - 2 * pad)} y1={pad} x2={pad + f * (W - 2 * pad)} y2={H - pad} />
          <line x1={pad} y1={pad + f * (H - 2 * pad)} x2={W - pad} y2={pad + f * (H - 2 * pad)} />
        </g>
      ))}
      <rect x={pad} y={pad} width={W - 2 * pad} height={H - 2 * pad} fill="none" stroke="#cfd8d3" strokeWidth={1} />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(p.lon)} cy={y(p.lat)} r={p.bad ? 12 : 7} fill={p.bad ? "rgba(179,38,30,.15)" : "rgba(31,138,91,.15)"} />
          <circle cx={x(p.lon)} cy={y(p.lat)} r={5} fill={p.bad ? "#b3261e" : "#1f8a5b"} stroke="#fff" strokeWidth={1.5} />
          <text x={x(p.lon)} y={y(p.lat) - 10} textAnchor="middle" fontSize={10} fill="#4a504a" fontFamily="'Space Grotesk',sans-serif">{p.label}</text>
        </g>
      ))}
    </svg>
  );
}
